/**
 * `external-sharing` Phase 5 Session 5.1 (2026-05-09).
 *
 * `/auth/callback?share=<id>` — the bridge route between Supabase's
 * magic-link invite flow and the server-side `linkSharesForUser` hook.
 *
 * Lifecycle:
 *
 *   1. Supabase's invite-redirect lands here AFTER the recipient consumes
 *      the magic link. The auth state populates asynchronously — the JWT
 *      lands in localStorage between Supabase's redirect and this route's
 *      mount, then `useAuth().user` resolves on a subsequent re-render.
 *   2. Once `useAuth().user` is non-null, fire `POST /api/auth/callback/share-link`
 *      with `{ shareId: search.share }`. Server `linkSharesForUser` is
 *      idempotent (Phase 1 Session 1.5) — re-running on an already-linked
 *      user is a no-op.
 *   3. On 200, navigate to `/deal/<primaryShareId>` (server's preferred-share
 *      hint, falling back to the URL `?share=` param).
 *   4. Terminal error states render an `<EmptyState>` card with a clear next
 *      action; no implicit redirects on errors.
 *
 * Why this lives at `/auth/callback` and NOT inside `/signup`: Supabase's
 * invite-redirect URL is a fixed contract (`${APP_URL}/auth/callback`). The
 * invite-token consumption happens at the Supabase URL — keeping the
 * callback route distinct keeps the auth-state-machine clear (signup is
 * for new account creation; callback is for post-invite session
 * resolution + share-linking).
 *
 * `__root.tsx`'s `isPublicRoute()` allowlist must include `/auth/callback`
 * (extended in this same session) — without the bypass, the auth-redirect
 * chain would bounce a freshly-magic-linked user back to `/login` and lose
 * the `?share=` query param, breaking the entire flow.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import * as Sentry from '@sentry/react';
import { useAuth } from '@/hooks/use-auth';
import { authCallbackShareLink } from '@/lib/shares-api';
import { ApiError } from '@/lib/api';
import { PublicChromeShell } from '@/components/deal';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';

/**
 * Search-param schema for the route. Both fields are optional + .catch
 * to undefined so a malformed `?share=` (non-UUID, e.g. truncated by an
 * email client) degrades to "no preferred share" rather than throwing
 * the entire route.
 *
 * `error` surfaces when Supabase's invite flow fails upstream (e.g.
 * expired magic link → redirected with `?error=expired`).
 */
export const authCallbackSearchSchema = z
  .object({
    share: z.string().uuid().optional().catch(undefined),
    error: z.string().optional().catch(undefined),
    // auth-flow-hardening Inc-3 (2026-05-20): `type` distinguishes the email-
    // link source so the callback can route to the correct downstream page.
    // Supabase email templates substitute this into the link URL.
    //   - `recovery`  → forward to `/reset-password` (preserves any
    //                   token_hash query param via window.location.search)
    //   - `signup`    → email-confirmation completes; navigate to `/cockpit`
    //                   (the cascade in `__root.tsx` routes from there based
    //                   on approval status)
    //   - `magiclink` → standard sign-in; navigate to `/cockpit`
    //   - `invite`    → admin-invited user; navigate to `/signup` so the
    //                   wizard runs (the trigger created an auth.users row
    //                   but the user_profiles row is pending — wizard fills it)
    //   - undefined   → defaults to the existing share-link bridge path
    //                   (Phase 5 Session 5.1 contract)
    type: z
      .enum(['recovery', 'signup', 'magiclink', 'invite'])
      .optional()
      .catch(undefined),
    // 2026-05-26: Supabase email-confirmation / magic-link / OAuth flows append
    // their own auth-protocol params to the callback URL. The route logic does
    // NOT read these — Supabase's `detectSessionInUrl: true` (lib/supabase.ts)
    // consumes them client-side via `exchangeCodeForSession` / `verifyOtp` and
    // then strips them via `window.history.replaceState`. We just need the
    // schema to ACCEPT them so the `.strict()` guard below doesn't throw
    // before Supabase gets a chance to strip. Without these declarations,
    // TanStack Router's validateSearch fires first, ZodError bubbles to the
    // route errorComponent, and the user sees the generic fallback. The race
    // between Supabase strip + router validate is what made it intermittent.
    code: z.string().optional().catch(undefined),
    token_hash: z.string().optional().catch(undefined),
    state: z.string().optional().catch(undefined),
    error_code: z.string().optional().catch(undefined),
    error_description: z.string().optional().catch(undefined),
  })
  .strict();

export type AuthCallbackSearch = z.infer<typeof authCallbackSearchSchema>;

type State =
  | 'waiting-for-session'
  | 'linking'
  | 'success'
  | 'expired'
  | 'gone'
  | 'unknown-error';

/** Single retry on 5xx with capped-exponential-style fixed delay. */
const RETRY_DELAY_MS = 1500;

export function AuthCallbackPage() {
  const search = useSearch({ strict: false }) as AuthCallbackSearch;
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Initial state respects an upstream `?error=expired` — short-circuit
  // before any session-wait or API call.
  const [state, setState] = useState<State>(() =>
    search.error === 'expired' ? 'expired' : 'waiting-for-session'
  );

  // Bumped by [Try again] to force the bridge effect to re-run. Without it,
  // the effect's deps don't change on retry — resetting hasFiredRef + state
  // alone wouldn't re-trigger the POST.
  const [retryAttempt, setRetryAttempt] = useState(0);

  // Guard against duplicate POSTs from re-renders. `useRef` (not `useState`)
  // because we don't need a re-render when this flips; we just need the
  // effect to bail on subsequent invocations.
  const hasFiredRef = useRef(false);
  // Retry-attempt counter for the single-retry-on-5xx contract. Lives on a
  // ref so the recursive `runLink` call doesn't need to thread it through
  // useState updates.
  const attemptRef = useRef(0);

  useEffect(() => {
    // auth-flow-hardening Inc-3 (2026-05-20): type-aware routing. The
    // bridge POST below is the SHARE-LINK path; recovery / signup /
    // magiclink / invite all skip the POST entirely and forward to the
    // appropriate downstream page.
    //
    // RECOVERY forwarded FIRST — before any session-wait — because the
    // recovery flow's session is RECOVERY-tier (Supabase fires
    // PASSWORD_RECOVERY event), and the reset-password page handles
    // both the session-wait AND the form locally. Forwarding eagerly
    // preserves the URL's `token_hash` query param + the hash if any.
    if (search.type === 'recovery') {
      // Preserve query string + hash so reset-password's verifyOtp +
      // detectSessionInUrl paths both work.
      const qs = window.location.search;
      const hash = window.location.hash;
      window.location.replace(`/reset-password${qs}${hash}`);
      return;
    }
    // Phase 1.5 hotfix (2026-05-25): the share-link bridge is gated on the
    // presence of `?share=<uuid>` — NOT on the absence of `?type=`. Reason:
    // `share` is the app-controlled signal (we set it ourselves when minting
    // invite redirect URLs in `inviteUserByEmail({ redirectTo:
    // '${APP_URL}/auth/callback?share=${shareId}' })`). `type` is
    // Supabase-controlled and may be absent depending on email-template
    // config or version — relying on its absence as the bridge trigger
    // mis-routed every magic-link sign-in lacking the type discriminator
    // to the "SHARE NOT AVAILABLE" gone-state.
    //
    // Routing precedence (top wins):
    //   1. share=<uuid> present → share-link bridge (recipient flow)
    //   2. type === 'invite'    → /signup (wizard runs)
    //   3. type === 'signup'    → /cockpit (post-confirmation)
    //   4. type === 'magiclink' → /cockpit
    //   5. no discriminator     → /cockpit (default for an authenticated
    //                              callback with no share + no type)
    if (!search.share) {
      if (authLoading) return;
      if (!user) {
        setState('expired');
        return;
      }
      const target = search.type === 'invite' ? '/signup' : '/cockpit';
      window.location.replace(target);
      return;
    }

    // Below: share-link bridge path — fires ONLY when ?share=<uuid> is present.
    // Already in a terminal/error state set at mount — don't fire.
    if (search.error === 'expired') return;
    // Wait for Supabase's getSession() probe to settle.
    if (authLoading) return;
    // Wait for the user to populate (Supabase's onAuthStateChange fires after
    // initial getSession on freshly-redirected sessions). If user is null
    // AFTER loading settles, the magic-link probably failed — treat as
    // expired so the user has a clear recovery path.
    if (!user) {
      // If we already kicked off the flow then lost the user, still bail.
      if (!hasFiredRef.current) {
        setState('expired');
      }
      return;
    }
    if (hasFiredRef.current) return;
    hasFiredRef.current = true;
    void runLink();

    async function runLink(): Promise<void> {
      setState('linking');
      try {
        const result = await authCallbackShareLink(search.share);
        // Gone-state: the user came via a specific share link but the
        // server linked NO shares to their account AND didn't return a
        // preferred share id. The clicked share is either revoked,
        // doesn't exist, or doesn't match this user's email — same
        // terminal-UX in all three cases (this share is no longer
        // available to you). Surface the gone-state card directly
        // rather than navigating to /deal/<id> + showing the deal
        // page's downstream "Deal Not Found" — keeps the user on the
        // bridge route with a single clear message.
        if (result.linkedCount === 0 && result.primaryShareId == null) {
          setState('gone');
          return;
        }
        const targetShareId = result.primaryShareId ?? search.share ?? null;
        if (!targetShareId) {
          setState('gone');
          return;
        }
        setState('success');
        navigate({
          to: '/deal/$shareId',
          params: { shareId: targetShareId },
          replace: true,
        });
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 401) {
            setState('expired');
            return;
          }
          if (err.status === 404) {
            setState('gone');
            return;
          }
          if (err.status >= 500 && attemptRef.current === 0) {
            attemptRef.current = 1;
            setTimeout(() => {
              void runLink();
            }, RETRY_DELAY_MS);
            return;
          }
        }
        // Unrecognized failure shape — surface to Sentry before falling through
        // to the user-visible terminal state. Tag `surface` to match the per-
        // site convention in `apps/web/src/lib/sentry.ts`.
        Sentry.captureException(err, {
          tags: { surface: 'auth-callback-share-link' },
        });
        setState('unknown-error');
      }
    }
  }, [
    user,
    authLoading,
    search.error,
    search.share,
    search.type,
    navigate,
    retryAttempt,
  ]);

  function handleTryAgain(): void {
    hasFiredRef.current = false;
    attemptRef.current = 0;
    setState('waiting-for-session');
    setRetryAttempt((n) => n + 1);
  }

  function goToLogin(): void {
    const next = search.share ? `/deal/${search.share}` : '/cockpit';
    // Plain location.assign bypasses TanStack Router's typed search-param
    // validation for the unrelated /login route (which doesn't declare a
    // `next` param in its schema). The full-page navigation is acceptable
    // for the expired-state error path — user is already mid-login flow.
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
  }

  if (state === 'expired') {
    return (
      <PublicChromeShell shareId={search.share}>
        <CenterShell>
          <EmptyState
            title="LINK EXPIRED"
            description="This invite link has expired. Sign in if you already have an account, or ask the deal owner for a fresh link."
            action={
              <Button variant="primary" onClick={goToLogin}>
                Sign in
              </Button>
            }
            supportLink={{ subject: 'Expired share link' }}
          />
        </CenterShell>
      </PublicChromeShell>
    );
  }

  if (state === 'gone') {
    return (
      <PublicChromeShell shareId={search.share}>
        <CenterShell>
          <EmptyState
            title="SHARE NOT AVAILABLE"
            description="This share is no longer available. The deal owner may have revoked access."
            action={
              <Button
                variant="secondary"
                onClick={() => navigate({ to: '/cockpit' })}
              >
                Back to Cockpit
              </Button>
            }
            supportLink={{ subject: 'Share link no longer available' }}
          />
        </CenterShell>
      </PublicChromeShell>
    );
  }

  if (state === 'unknown-error') {
    return (
      <PublicChromeShell shareId={search.share}>
        <CenterShell>
          <EmptyState
            title="SOMETHING WENT WRONG"
            description="Something went wrong on our end. Try again, or contact support if the problem persists."
            action={
              <Button variant="primary" onClick={handleTryAgain}>
                Try again
              </Button>
            }
            supportLink={{ subject: 'Auth callback error' }}
          />
        </CenterShell>
      </PublicChromeShell>
    );
  }

  // waiting-for-session | linking | success share the same loading shell —
  // the body copy progresses to give the user a sense of motion. `success`
  // is the brief moment between the 200 response and the navigate landing.
  const description =
    state === 'success'
      ? 'Linked! Redirecting…'
      : search.share
      ? 'Linking your account…'
      : 'Checking for any pending shares for your account…';

  return (
    <PublicChromeShell shareId={search.share}>
      <CenterShell>
        <EmptyState title="LINKING ACCOUNT" description={description} />
      </CenterShell>
    </PublicChromeShell>
  );
}

function CenterShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex-1 flex items-center justify-center">{children}</div>
  );
}
